import React, { ReactNode, useEffect, useRef, useState } from 'react';
import AudioRecorder from '../../../audio/AudioRecorder';
import { Typography, Card, Button } from '@mui/material';
import './SubTaskView.css';
import { ProfileName, SubTask } from './models';
import AudioGainSlider from '../../../audio/AudioGainSlider';
import AudioLevelDisplay from '../../../audio/AudioLevelDisplay';
import { useLocalStorage } from '../../../../services/LocalStorageProvider';
import { translateReactNode } from '../../../../utils/translateReactNode';

const DEFAULT_VOICE_GAIN = 0.55;
const DEFAULT_BREATHE_GAIN = 0.9;
const DEFAULT_COUGH_GAIN = 0.1;

export interface GainProfile {
  name: ProfileName,
  value: number
}

const initializeGain = (profileName: ProfileName, gainProfiles: [GainProfile]) => {

  if (gainProfiles && gainProfiles.length > 0) {
    const profile = gainProfiles.find(x => x.name === profileName);
    if (profile) {
      return profile.value;
    }
  }

  switch (profileName) {
    case 'speech':
      return DEFAULT_VOICE_GAIN;
    case 'breathe':
      return DEFAULT_BREATHE_GAIN;
    case 'cough':
      return DEFAULT_COUGH_GAIN;
    default:
      return 1;
  }
};

interface SubTaskViewProps {
  children: ReactNode;
  onAudioRecorded: (audioSource: string) => void;
  onIsRecordingChange?: (isRecording: boolean) => void;
  onStreamChange?: (stream: MediaStream) => void;
  showAudioPreview?: boolean;
  showAudioBar?: boolean;
  subTask: SubTask;
  buttonLabel?: string;
}



const SubTaskView: React.FC<SubTaskViewProps> = ({
  subTask,
  children,
  onAudioRecorded,
  onIsRecordingChange,
  onStreamChange,
  showAudioPreview = true,
  showAudioBar = false,
  buttonLabel = "Next",
}) => {
  const audioPreviewRef = useRef<HTMLAudioElement>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const originalStreamRef = useRef<MediaStream | null>(null);
  const { storage: localStorage, updateStorage: updateLocalStorage } = useLocalStorage();

  const [audioSource, setAudioSource] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const nextButtonRef = useRef<HTMLButtonElement | null>(null);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [gain, setGain] = useState(() => initializeGain(subTask.profileName, localStorage.gainProfiles));
  const [device, setDevice] = useState<MediaDeviceInfo | null>(null);
  const [disableRecording, setDisableRecording] = useState<boolean>(() => localStorage.disableRecording || false);
  const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(false);

  const { stopwatchSeconds: timer, title, instructions, content, recordButtonDisabled, showGainSlider, buttonDisabled } = subTask;

  useEffect(() => {
    const init = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const device = devices.find(x=> x.kind === 'audioinput' && x.label.startsWith('Default - '));
      let stream: MediaStream;
      if (device) {
        setDevice(device);
        stream = await navigator.mediaDevices.getUserMedia({ audio: { advanced: [ {deviceId: device.deviceId} ] } });
      } else {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      originalStreamRef.current = stream;
      const tracks = stream.getAudioTracks();

      tracks.map(async (track) => {
        try {
          console.log('track capabilities', track.getCapabilities());
          await track.applyConstraints({
            autoGainControl: false,
            channelCount: 1,
            noiseSuppression: false,
            echoCancellation: false,
            sampleRate: 44100,
            sampleSize: 16
          });
          console.log('track constraints', track.getConstraints());
        } catch (e) {
          console.error(e);
        }
      });

      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(new MediaStream(tracks));

      const gainNode = gainNodeRef.current = audioContext.createGain();
      gainNode.gain.value = gain;
      source.connect(gainNode);

      const destination = audioContext.createMediaStreamDestination();
      gainNode.connect(destination);

      setStream(destination.stream);
      onStreamChange?.(destination.stream);
    }

    init();

    if (localStorage.isRecording) {
      updateLocalStorage({ isRecording: false });
    }
    if (localStorage.disableRecording) {
      updateLocalStorage({ disableRecording: false });
    }

    // Set createdAt date if not present
    if (!subTask.createdAt) {
      subTask.createdAt = new Date(Date.now()).toISOString();
    }

    return () => {
      if (originalStreamRef.current) {
        originalStreamRef.current.getAudioTracks().forEach(track => track.stop());
        originalStreamRef.current = null;
      }
      updateLocalStorage({ isRecording: false, disableRecording: false });
      stopPlayingAudioPreview();
    }
  }, []);

  useEffect(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = gain;
    }

    updateGainProfile(subTask.profileName, gain);
  }, [gain]);

  useEffect(() => {
    stream?.getAudioTracks().forEach(track => track.stop());
  }, [showGainSlider]);

  useEffect(() => {
    if (isRecording) {
      stopPlayingAudioPreview();
    }

    updateLocalStorage({ isRecording });
    onIsRecordingChange?.(isRecording);
  }, [isRecording]);

  useEffect(() => {
    if (!isRecording && audioSource && nextButtonRef.current) {
      nextButtonRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isRecording, audioSource]);

  useEffect(() => {
    if (localStorage.disableRecording !== disableRecording) {
      setDisableRecording(localStorage.disableRecording);
    }
  }, [localStorage.disableRecording]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const checkButtonDisabled = async () => {
      let isDisabled = false;
      if (buttonDisabled) {
        isDisabled = await buttonDisabled(subTask, audioSource || undefined);
      }
      if (isDisabled !== nextButtonDisabled) {
        setNextButtonDisabled(isDisabled);
      }
    }
    checkButtonDisabled();
  }, [audioSource, buttonDisabled, subTask, nextButtonDisabled]);

  const stopPlayingAudioPreview = () => {
    if (audioPreviewRef.current) {
      audioPreviewRef.current.pause();
      audioPreviewRef.current.currentTime = 0;
    }
  };

  const updateGainProfile = (name: ProfileName, gain: number) => {
    const gainProfiles: [GainProfile] = localStorage.gainProfiles || [];
    const profile = gainProfiles.find(x => x.name === name);
    if (profile) {
      if (gain !== profile.value) {
        console.log('Updating gain profile', name, gain);
        profile.value = gain;
        updateLocalStorage({ ...gainProfiles });
      }
    } else {
      console.log('Adding new gain profile', name, gain);
      gainProfiles.push({ name, value: gain });
      updateLocalStorage({ gainProfiles });
    }
  }

  const handleOnAudioRecorded = async (blob: Blob) => {
    setAudioSource(URL.createObjectURL(blob));
    subTask.inputGain = gain;
    subTask.microphone = `Web Audio: ${device?.label || 'Unknown Device'}`;
  };

  return (
    <div className='subTask'>
      {title && <Typography variant='h4' component='h2' className='subtask-title' >{title}</Typography>}
      {instructions && <Typography variant='h5' component='div' className='subtask-instructions'>{instructions}</Typography>}

      {content && (
        <div style={{ alignContent: 'center', textAlign: 'center' }} className='subtask-content'>
          {content}
        </div>
      )}

      <div>{children}</div>

      <div style={{ marginTop: '1rem', textAlign: 'center' }}>

        {stream && (
          <>
            <div className={recordButtonDisabled || disableRecording ? 'disabled' : ''}>
              <AudioRecorder
                stream={stream}
                onAudioRecorded={handleOnAudioRecorded}
                handleIsRecording={setIsRecording}
                timer={timer} />
            </div>

            {showGainSlider && <AudioGainSlider gain={gain} setGain={setGain} />}
            {showAudioBar && <AudioLevelDisplay stream={stream} />}
          </>
        )}
        {showAudioPreview && audioSource && (
          <Card className={"audio-preview " + (isRecording ? "disabled": "")} >
            <Typography variant="h6" component='div'>{translateReactNode("Audio Preview")}</Typography>
            <audio ref={audioPreviewRef} controls src={audioSource}></audio>
          </Card>
        )}
      </div>

      {audioSource && (
        <Button variant="contained"
          onClick={() => onAudioRecorded(audioSource)}
          disabled={isRecording || !audioSource || nextButtonDisabled}
          style={{
            marginTop: '1rem'
          }}
        >
          {translateReactNode(buttonLabel)}
        </Button>
      )}
    </div>
  );
};

export default SubTaskView;
